import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { getPhoto, deletePhoto, getAllPhotos } from './photoDB';
import { getGlobalDealerName } from './globalState';
import { getBrand } from './brands';
import { MAX_PHOTOS } from './limits';

/**
 * Convert a Base64 data URL string into a File object so it can be sent
 * via multipart/form-data to the backend.
 */
function dataURLtoFile(dataURL, filename) {
  const [header, data] = dataURL.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new File([array], filename, { type: mime });
}

function PhotoGallery() {
  /*
  PhotoGallery component allows users to view, select, and delete photos.
  It displays all photos stored in IndexedDB, allows selection of up to
  MAX_PHOTOS photos, and provides options to delete photos or submit selected photos.

  Routing on submit depends on how the user arrived:
    source === 'license-plate'  →  /license-plate-confirm  (plate upload flow)
    source === 'backgrounds'    →  /photo-confirm           (dealership photo flow)
  */
  const location = useLocation();
  const navigate = useNavigate();
  const initialPhotoKeys = location.state?.photoKeys || [];
  const initialSelectedPhotoKeys = location.state?.selectedPhotoKeys || [];
  const phototype = location.state?.source || 'backgrounds';
  // 'frame' when photographing a custom frame via UploadFrame;
  // 'plate' (default) for normal license plate photography.
  const assetType = location.state?.assetType || 'plate';

  const [photoKeys, setPhotoKeys] = useState(initialPhotoKeys);
  const [photos, setPhotos] = useState([]);
  const [selectedPhotoKeys, setSelectedPhotoKeys] = useState(initialSelectedPhotoKeys);
  const [showVehiclePaths, setShowVehiclePaths] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const vehiclePathOverlay = getBrand(getGlobalDealerName())?.overlay;

  useEffect(() => {
    async function loadKeysIfNeeded() {
      if (!initialPhotoKeys.length) {
        const keys = await getAllPhotos();
        setPhotoKeys(keys);
      }
    }
    loadKeysIfNeeded();
  }, []);
  
  useEffect(() => {
    async function fetchPhotos() {
      const loadedPhotos = await Promise.all(photoKeys.map(key => getPhoto(key)));
      setPhotos(loadedPhotos);
    }
    if (photoKeys.length) fetchPhotos();
  }, [photoKeys]);

  const togglePhotoSelection = (photoKey) => {
    setSelectedPhotoKeys((prevSelected) => {
      if (prevSelected.includes(photoKey)) {
        return prevSelected.filter((k) => k !== photoKey);
      } else if (prevSelected.length < MAX_PHOTOS) {
        return [...prevSelected, photoKey];
      } else {
        alert(`You can only select up to ${MAX_PHOTOS} photos.`);
        return prevSelected;
      }
    });
  };

  const handleDeletePhoto = async (key) => {
    await deletePhoto(key);
    setPhotoKeys(prev => prev.filter(k => k !== key));
    setSelectedPhotoKeys(prev => prev.filter(k => k !== key));
  };

  const handleSubmitPhotos = async () => {
    if (selectedPhotoKeys.length === 0) {
      alert('Please select at least one photo to submit.');
      return;
    }

    if (phototype === 'license-plate') {
      // Convert selected IndexedDB photos to File objects so
      // LicencePlateConfirm can send them as multipart/form-data.
      setIsSubmitting(true);
      try {
        const loadedPhotos = await Promise.all(
          selectedPhotoKeys.map(key => getPhoto(key))
        );
        const files = loadedPhotos.map((dataURL, i) =>
          dataURLtoFile(dataURL, `plate_${i + 1}.jpg`)
        );
        navigate('/license-plate-confirm', {
          state: {
            files,
            // Pass all session keys so ThankYou can clean up IndexedDB
            allPhotoKeys: photoKeys,
            assetType,
          },
        });
      } catch (err) {
        console.error('Failed to prepare plate photos:', err);
        alert('An error occurred preparing your photos. Please try again.');
        setIsSubmitting(false);
      }
    } else {
      navigate('/photo-confirm', {
        state: {
          photoKeys,
          selectedPhotoKeys,
        }
      });
    }
  };

  return (
    <div className="PhotoGallery">
      <h1 className="PhotoGallery-title">Select Up To {MAX_PHOTOS} Photos To Send:</h1>
      <div className="PhotoGallery-grid">
        {photos.map((photo, idx) => {
          const key = photoKeys[idx];
          return (
            <div
              key={key}
              className={`PhotoGallery-item ${selectedPhotoKeys.includes(key) ? 'selected' : ''}`}
              onClick={() => togglePhotoSelection(key)}
              style={{
                position: 'relative',
                aspectRatio: '4 / 3',
                overflow: 'hidden',
              }}
            >
              <img
                src={photo}
                alt={`Photo ${idx + 1}`}
                className="PhotoGallery-image"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  aspectRatio: '4 / 3'
                }}
              />
              {vehiclePathOverlay && showVehiclePaths && (
                <img
                  src={vehiclePathOverlay}
                  alt="Vehicle Path Overlay"
                  className="PhotoGallery-path-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover', // Crop overlay to 4:3
                    aspectRatio: '4 / 3',
                    pointerEvents: 'none',
                    zIndex: 2,
                    opacity: 0.7,
                  }}
                />
              )}
              <button
                className="PhotoGallery-delete"
                onClick={e => {
                  e.stopPropagation();
                  handleDeletePhoto(key);
                }}
                aria-label="Delete photo"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
      <div className="PhotoGallery-actions">
        {vehiclePathOverlay && phototype === 'backgrounds' && (
          <button
            className="App-button"
            onClick={() => setShowVehiclePaths(prev => !prev)}
          >
            {showVehiclePaths ? 'Hide Vehicle Overlay' : 'Show Vehicle Overlay'}
          </button>
        )}
        <button
          className="App-button"
          onClick={() =>
            navigate(phototype === 'license-plate' ? '/license-plate-photography' : '/photography', {
              state: phototype === 'license-plate' ? { assetType } : { photoKeys }
            })
          }
        >
          Back to Camera
        </button>
        <button className="App-button" onClick={handleSubmitPhotos} disabled={isSubmitting}>
          Submit Photos
        </button>
      </div>
    </div>
  );
}

export default PhotoGallery;
